import { create } from "zustand";
import { scopedIdbGet as idbGet, scopedIdbSet as idbSet } from "@/lib/client-scoped-idb";

/**
 * Pre-workshop questionnaire answers — per-workshop, per-browser.
 *
 * UAT round 1 found two symptoms of the same root cause: answers lived in
 * `QuestionnaireModal`'s local `useState` (gone on navigation/reload — "unable
 * to view the answers"), and the Brief row's status was hardcoded `"Complete"`
 * in `content.ts` ("questionnaire always showing Complete"). This store fixes
 * both: answers persist to IndexedDB and the Brief row derives its status from
 * what's actually answered.
 *
 * Deliberately IDB-only (no BFF state-doc): answers are the *respondent's*
 * drafts, not shared team content — a shared JSON blob would clobber different
 * users' responses. Backend submission remains a later phase (see the
 * QuestionnaireModal jsdoc); this store is the local draft surface that phase
 * will submit from.
 */

const IDB_KEY = "xaes-questionnaires";

/** workshopId → questionId → answer text. */
type AnswersByWorkshop = Record<string, Record<string, string>>;

type QuestionnairesState = {
  answersByWorkshop: AnswersByWorkshop;
  isHydrated: boolean;
  hydrate: () => Promise<void>;
  setAnswer: (workshopId: string, questionId: string, value: string) => void;
};

function sanitise(raw: unknown): AnswersByWorkshop {
  const out: AnswersByWorkshop = {};
  if (typeof raw !== "object" || raw === null) return out;
  for (const [wsId, answers] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof answers !== "object" || answers === null) continue;
    const clean: Record<string, string> = {};
    for (const [qId, value] of Object.entries(answers as Record<string, unknown>)) {
      if (typeof value === "string") clean[qId] = value;
    }
    out[wsId] = clean;
  }
  return out;
}

export const useQuestionnaires = create<QuestionnairesState>((set, get) => ({
  answersByWorkshop: {},
  isHydrated: false,
  hydrate: async () => {
    if (get().isHydrated) return;
    try {
      const raw = await idbGet(IDB_KEY);
      set({ answersByWorkshop: sanitise(raw), isHydrated: true });
    } catch {
      set({ isHydrated: true });
    }
  },
  setAnswer: (workshopId, questionId, value) => {
    const next: AnswersByWorkshop = {
      ...get().answersByWorkshop,
      [workshopId]: {
        ...get().answersByWorkshop[workshopId],
        [questionId]: value,
      },
    };
    set({ answersByWorkshop: next });
    // Best-effort write-through; the in-memory copy is already current.
    void idbSet(IDB_KEY, next).catch(() => {});
  },
}));
